import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '@praxis/component-auth';
import { useInstallerAuthContext } from '../../hooks/useInstallerAuthContext';

import {
  selectFriendlyNames,
  readerInfo,
  selectSortedReaders,
  lookupDevicesToCalibrate,
  lookupDevicesToCalibrateExt,
} from '../../app/calibration/startCalibrationSlice';
import { clearReaderCalibration } from '../../app/calibration/calibrationSlice';
import {
  setActiveReader,
  getMap,
  getMapExt,
  selectMapByStore,
} from '../../app/neptune/neptuneSlice';
import { convertReaderNamesToSelectOptions } from '../../utils/helpers';
import { getMacAddress } from '../../utils/tagCalibrationHelpers';

import {
  Grid,
  Image,
  Chip,
  Divider,
  Heading,
} from '@enterprise-ui/canvas-ui-react';
import { LayoutTagWalk } from '../LayoutTagWalk';
import { NeptuneMap } from '../../components/NeptuneMap';
import { Button, SelectInput } from 'ui-library';
import { Message } from '../../components/Message';
import { CalibrationStatus } from '../../components/CalibrationStatus';
import mapPlaceholder from '../../common/graphics/map-placeholder.svg?url';

import styles from './TagWalkPage.module.scss';
import { RefreshIcon } from '@enterprise-ui/icons';

export const SetupReaderPage = () => {
  const auth = useAuth();
  const { session } = auth;
  const { user } = useInstallerAuthContext();
  const dispatch = useDispatch();
  let hostname = window.location.hostname;

  const location = useSelector(
    (state) => state.calibration.activeReaderDetails.storeId,
  );
  const floorNum = useSelector((state) => state.neptune.storeFloor);
  const rfidReaders = useSelector((state) =>
    selectFriendlyNames(state, location, floorNum),
  );
  const readersInStore = useSelector((state) =>
    selectSortedReaders(state, location),
  );
  const mapSelector = useSelector((state) =>
    selectMapByStore(state, location, floorNum),
  );

  const [token, setToken] = useState('');
  const [pageStatus, setPageStatus] = useState('');
  const [nextPage, setNextPage] = useState('');
  const [selectedReader, setSelectedReader] = useState('');
  const [calibrationInfo, setCalibrationInfo] = useState('');
  const [friendlyLocation, setFriendlyLocation] = useState('Unknown');
  const [deviceHealth, setDeviceHealth] = useState('');
  const [macAddress, setMacAddress] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [mountType, setMountType] = useState('');
  const [isCalibrationReady, setIsCalibrationReady] = useState('');

  let selectedReaderDetails = readersInStore?.get(selectedReader);

  const handleRequestDispatch = useCallback(() => {
    let selectedReaderMac = getMacAddress(selectedReader, readersInStore);
    dispatch(clearReaderCalibration(selectedReaderMac)); // Clears calibration data from Redux store (if any)
  }, [selectedReader, dispatch, readersInStore]);

  useEffect(
    function setExternalToken() {
      if (user) {
        let { token } = user; // External user token
        setToken(token);
      } else if (session && hostname === 'iotinstall.target.com') {
        let token = localStorage.getItem('access_token'); // TM token
        setToken(token);
      }
    },
    [user, hostname, session],
  );

  useEffect(
    function dispatchMapRequest() {
      if (location) {
        let storeId = location.substr(1);
        if (user || hostname === 'iotinstall.target.com') {
          dispatch(getMapExt({ storeId, floorNum, token }));
        } else {
          dispatch(getMap({ storeId, floorNum }));
        }
      }
    },
    [dispatch, location, token, user, hostname, floorNum],
  );

  useEffect(
    function setupNavReset() {
      if (pageStatus === 'active') {
        setNextPage('/calibration');
      }
    },
    [pageStatus, dispatch],
  );

  useEffect(() => {
    if (selectedReader) {
      dispatch(setActiveReader(selectedReader)); // used in neptune map map

      handleRequestDispatch();
      setTimeout(() => {
        requestAnimationFrame(() => {
          window.scrollTo({
            top: document.documentElement.scrollHeight - window.innerHeight,
            left: 0,
            behavior: 'smooth',
          });
        });
      }, 3);
    }
  }, [selectedReader, dispatch, handleRequestDispatch]);

  useEffect(
    function setActiveReaderInfo() {
      if (selectedReaderDetails) {
        let {
          host_name,
          mac_address,
          friendly_location,
          device_is_healthy,
          mount_type,
          device_type,
          calibration_readiness,
          last_calibration_date,
          last_calibration_status,
        } = selectedReaderDetails;

        dispatch(
          readerInfo({
            [selectedReader]: {
              host_name,
              mac_address,
              friendly_location,
              mount_type,
              device_type,
            },
          }),
        );

        setCalibrationInfo({ last_calibration_status, last_calibration_date });
        setFriendlyLocation(friendly_location);
        setDeviceHealth(device_is_healthy);
        setMacAddress(mac_address);
        setDeviceType(device_type);
        setMountType(mount_type);
        setIsCalibrationReady(calibration_readiness);
      }
    },
    [selectedReader, dispatch, selectedReaderDetails],
  );

  return (
    <>
      <LayoutTagWalk
        pageTitle="Reader Calibration | Setup"
        nextPage={nextPage}
        prevPage={'/calibrationSetupTag'}
        nextBtnLabel="Start calibration"
        activeSetup={true}
        btnDisabled={
          !deviceHealth ||
          isCalibrationReady === 'FAILED' ||
          isCalibrationReady === 'IN_PROGRESS'
        }
      >
        <Grid.Item xs={12} md={6}>
          <SelectInput
            id="displayName"
            label="Select reader to calibrate"
            name="displayName"
            options={convertReaderNamesToSelectOptions(rfidReaders)}
            required
            onChange={(e) => {
              setSelectedReader(e.value);
              setPageStatus('active');
              if (session) {
                dispatch(lookupDevicesToCalibrate(location));
              } else if (user) {
                dispatch(lookupDevicesToCalibrateExt({ location, token }));
              }
            }}
            className="hc-mb-xl"
            aria-labelledby={`displayNameLabel`}
            classNamePrefix="customSelect"
          />
        </Grid.Item>
        <Grid.Item xs={12}>
          {typeof mapSelector === 'string' &&
          mapSelector?.includes('Unavailable') ? (
            <div className={styles.mapPlaceholderWrapper}>
              <Image
                src={mapPlaceholder}
                alt="Interactive map is unavailable"
                width="auto"
                height="265px"
                type="contain"
              />
            </div>
          ) : (
            <NeptuneMap storeId={location} />
          )}
        </Grid.Item>
        {selectedReader && (
          <Grid.Item xs={11} sm={6} className="hc-mt-md left-flex-block">
            <Heading size={5}>Device Info</Heading>
            <Divider />
            <div className={styles.row}>
              <span className={styles.label}>Mac Address:</span>
              <strong className={styles.value}>{macAddress}</strong>
            </div>
            <div className={styles.row}>
              <span className={styles.label}>Device Type:</span>
              <strong className={styles.value}>{deviceType}</strong>
            </div>
            <div className={styles.row}>
              <span className={styles.label}>Mount Type:</span>
              <strong className={styles.value}>{mountType}</strong>
            </div>
            <div className={styles.row}>
              <span className={styles.label}>Located in:</span>
              <strong className={styles.value}>{friendlyLocation}</strong>
            </div>
            <Heading size={5} className="hc-mt-md">
              Status
            </Heading>
            <Divider />
            {calibrationInfo && Object.keys(calibrationInfo).length > 0 && (
              <CalibrationStatus data={calibrationInfo} />
            )}
            <p className={styles.row}>
              <span className={styles.label}>Health status:</span>
              <strong className={styles.value}>
                {deviceHealth !== undefined &&
                  (deviceHealth ? 'Healthy' : 'Unhealthy')}
                {deviceHealth === undefined && 'Unknown'}
              </strong>
            </p>
            <p className={styles.row}>
              <span className={styles.label}>Calibration Readiness:</span>
              <span className={styles.value}>
                <Chip
                  size="normal"
                  color={
                    isCalibrationReady === 'READY'
                      ? 'success'
                      : isCalibrationReady === 'FAILED'
                        ? 'error'
                        : 'alert'
                  }
                >
                  {isCalibrationReady === 'READY'
                    ? 'READY'
                    : isCalibrationReady === 'FAILED'
                      ? 'NOT READY'
                      : 'IN PROGRESS'}
                </Chip>
                {isCalibrationReady === 'IN_PROGRESS' && (
                  <Button
                    icon={RefreshIcon}
                    name="icon"
                    label={''}
                    onClick={() => {
                      if (session) {
                        dispatch(lookupDevicesToCalibrate(location));
                      } else if (user) {
                        dispatch(
                          lookupDevicesToCalibrateExt({ location, token }),
                        );
                      }
                    }}
                  />
                )}
              </span>
            </p>

            {(!deviceHealth ||
              isCalibrationReady === 'FAILED' ||
              isCalibrationReady === 'IN_PROGRESS') && (
              <Message type="error">
                <p className="hc-mt-xs hc-ta-center">
                  The selected device is currently unavailable. Please choose
                  another device or try again later.
                </p>
              </Message>
            )}
          </Grid.Item>
        )}
      </LayoutTagWalk>
    </>
  );
};
